<template>
  <div :class="containerClass">
    <page-header
      :title="this.$route.params.id ? 'Edit University' : 'Add University'"
      :breadcrumb="breadcrumb"
      :container-class="null"
      class="mb-32pt"
    />

    <page-separator title="University Info" />

    <b-form class="col-md-12 px-0 page-section pt-0" @submit.prevent="onSubmit">
      <!-- Basic Uni Info -->

      <b-form-group label-for="small_logo_url" class="row-align-items-center">
        <label class="form-label">
          Small Logo
          <i
            class="material-icons icon-16pt clickable-item text-muted"
            v-b-popover.hover.top="
              `Ideal dimensions for small logo should be 512*512. This will be shown as app's favicon.`
            "
            >info</i
          >
        </label>
        <b-media class="align-items-center" vertical-align="center">
          <!-- <user-avatar slot="aside" rounded="lg" size="lg" :isLoading="isLogoUploading" :file-url="owner.logoUrl">
          </user-avatar> -->

          <fmv-avatar :title="true" rounded size="lg" no-link slot="aside">
            <span v-if="isSmallLogoUploading">...</span>
            <b-img :src="uni.small_logo_url" class="img-fluid" width="40" alt="Logo" v-else-if="uni.small_logo_url" />
            <i class="fas fa-university fa-lg" v-else></i>
          </fmv-avatar>

          <image-uploader
            @image="e => setImage(e, 'smallLogo')"
            :isUploading="isSmallLogoUploading"
            :isRequired="!uni.small_logo_url"
            placeholder="Select University's Small Logo Picture"
          />
        </b-media>
      </b-form-group>

      <b-form-group class="row-align-items-center">
        <label class="form-label">
          Campus Image
          <i
            class="material-icons icon-16pt clickable-item text-muted"
            v-b-popover.hover.top="`Ideal dimensions for the image should be 1280*720.`"
            >info</i
          >
        </label>
        <b-media class="align-items-center" vertical-align="center">
          <!-- <user-avatar slot="aside" rounded="lg" size="lg" :isLoading="isLogoUploading" :file-url="owner.logoUrl">
          </user-avatar> -->

          <fmv-avatar :title="true" rounded size="lg" no-link slot="aside">
            <span v-if="isLogoUploading">...</span>
            <b-img :src="uni.logoUrl" class="img-fluid" width="40" alt="Logo" v-else-if="uni.logoUrl" />
            <i class="fas fa-university fa-lg" v-else></i>
          </fmv-avatar>

          <image-uploader
            @image="e => setImage(e, 'logo')"
            :isUploading="isLogoUploading"
            :isRequired="!uni.logoUrl"
            placeholder="Select University's Campus Picture"
          />
        </b-media>
      </b-form-group>

      <b-form-group label="Name" label-for="name" label-class="form-label">
        <b-form-input id="name" placeholder="Enter University's Name" v-model="uni.name" required />
      </b-form-group>

      <b-form-group label="Contact email" label-for="contact_email" label-class="form-label">
        <b-form-input
          id="contact_email"
          placeholder="Enter University's contact email"
          v-model="uni.contact_email"
          type="email"
        />
      </b-form-group>

      <b-form-group label="Community Link" label-for="community-link" label-class="form-label">
        <b-form-input id="community-link" placeholder="Enter community link" v-model="communityLink" />
      </b-form-group>

      <b-form-group label="Phone" label-for="phone" label-class="form-label">
        <b-form-input
          id="phone"
          placeholder="(888) 689 - 1235"
          v-model="uni.phone"
          v-mask="'(###) ### - ####'"
          required
        />
      </b-form-group>

      <!-- Address Fields -->
      <b-form-group :label="$t('schoolMsgs.address')" label-for="address" label-class="form-label">
        <b-form-input id="address" :placeholder="$t('schoolMsgs.address')" v-model="uni.address" required />
      </b-form-group>

      <div class="row">
        <b-form-group :label="$t('schoolMsgs.zipcode')" label-for="zipcode" label-class="form-label" class="col-md-6">
          <b-form-input id="zipcode" :placeholder="$t('schoolMsgs.zipcode')" v-model="uni.zipcode" required />
        </b-form-group>

        <b-form-group :label="$t('schoolMsgs.city')" label-for="city" label-class="form-label" class="col-md-6">
          <b-form-input id="city" :placeholder="$t('schoolMsgs.city')" v-model="uni.city" required />
        </b-form-group>
      </div>

      <div class="row">
        <b-form-group :label="$t('schoolMsgs.state')" label-for="state" label-class="form-label" class="col-md-6">
          <v-select
            id="state"
            class="form-control v-select-custom"
            label="text"
            v-model="uni.state"
            :reduce="state => state.value"
            placeholder="Select State"
            :options="stateOptions"
          >
            <template #search="{ attributes, events }">
              <input class="vs__search" :required="!uni.state" v-bind="attributes" v-on="events" />
            </template>
          </v-select>
        </b-form-group>

        <b-form-group :label="$t('schoolMsgs.country')" label-for="country" label-class="form-label" class="col-md-6">
          <v-select
            id="country"
            class="form-control v-select-custom"
            label="text"
            disabled
            v-model="uni.country"
            :reduce="country => country.value"
            :placeholder="$t('schoolMsgs.countryPlaceholder')"
            :options="countryOptions"
          >
            <template #search="{ attributes, events }">
              <input class="vs__search" :required="!uni.country" v-bind="attributes" v-on="events" />
            </template>
          </v-select>
        </b-form-group>
      </div>

      <!-- Other Details -->

      <b-form-group label="Short Bio" label-for="short_bio" label-class="form-label">
        <b-form-textarea
          id="short_bio"
          placeholder="Enter University's Short/Summarized Bio."
          :rows="5"
          v-model="uni.short_bio"
        />
      </b-form-group>
      <b-form-group label="Bio" label-for="bio" label-class="form-label">
        <editor
          :init="{
            height: 400,
            menubar: 'edit view insert format tools table help',
            plugins: [
              'advlist autolink lists link image charmap print preview anchor',
              'searchreplace visualblocks code fullscreen',
              'insertdatetime media table paste code help wordcount',
            ],
            toolbar:
              'undo redo | formatselect | bold italic backcolor | \
           alignleft aligncenter alignright alignjustify | \
           bullist numlist outdent indent | removeformat | help',
          }"
          v-model="uni.bio"
          placeholder="Enter University's Bio"
        />
      </b-form-group>

      <!-- <b-form-group
        :label="$t('schoolMsgs.profileBgImage')"
        label-for="profileBgImage"
        label-class="form-label"
        class="row-align-items-center"
      >
        <b-media class="align-items-center" vertical-align="center">
          <b-form-file
            id="profileBgImage"
            placeholder="Select University's Profile Background Image"
            :browse-text="$t('generalMsgs.browse')"
            v-model="uploadFile.profileBgImage"
            @input="uploadImage('profileBgImage')"
            :disabled="isProfileBgImgUploading"
            accept="image/*"
          />
        </b-media>
      </b-form-group> -->

      <b-form-group label="Sponsorships" label-for="scholarships" label-class="form-label">
        <v-select
          id="scholarships"
          class="form-control v-select-custom"
          label="title"
          v-model="uni.scholarships"
          :reduce="sch => sch"
          placeholder="Select Sponsorship"
          :options="allScholarships"
          multiple
          :loading="areSchLoading"
        >
          <template #search="{ attributes, events }">
            <input class="vs__search" :required="!uni.scholarships" v-bind="attributes" v-on="events" />
          </template>
          <template slot="option" slot-scope="option">
            <span>{{ option.title }}</span>
          </template>
        </v-select>
      </b-form-group>

      <div class="row">
        <b-form-group
          :label="$t('schoolMsgs.primaryColor')"
          label-for="primaryColor"
          label-class="form-label"
          class="col-md-4"
        >
          <v-swatches
            v-model.trim="uni.primaryColor"
            shapes="circles"
            show-fallback
            :swatches="swatches"
            fallback-input-type="color"
            :fallback-ok-text="$t('generalMsgs.select')"
            fallback-input-class="py-0"
          ></v-swatches>
        </b-form-group>
        <b-form-group
          :label="$t('schoolMsgs.secondaryColor')"
          label-for="secondaryColor"
          label-class="form-label"
          class="col-md-4"
        >
          <v-swatches
            v-model.trim="uni.secondaryColor"
            shapes="circles"
            show-fallback
            :swatches="swatches"
            fallback-input-type="color"
            :fallback-ok-text="$t('generalMsgs.select')"
            fallback-input-class="py-0"
          ></v-swatches>
        </b-form-group>
        <b-form-group
          :label="$t('schoolMsgs.darkColor')"
          label-for="darkColor"
          label-class="form-label"
          class="col-md-4"
        >
          <v-swatches
            v-model.trim="uni.darkColor"
            shapes="circles"
            show-fallback
            :swatches="swatches"
            fallback-input-type="color"
            :fallback-ok-text="$t('generalMsgs.select')"
            fallback-input-class="py-0"
          ></v-swatches>
        </b-form-group>
      </div>
      <b-form-group :label="$t('schoolMsgs.website')" label-for="website" label-class="form-label">
        <b-form-input id="website" :placeholder="$t('schoolMsgs.websitePlaceholder')" v-model="uni.website" />
      </b-form-group>
      <b-form-group label="Institution Type" label-for="institution_type" label-class="form-label">
        <b-form-select
          id="institution_type"
          v-model="uni.institution_type"
          :options="[
            { value: null, text: 'Select institution type' },
            { value: 'university', text: 'University (4-year)' },
            { value: 'community_college', text: 'Community College (2-year)' },
            { value: 'technical_college', text: 'Technical / Trade College' },
          ]"
        />
      </b-form-group>
      <b-form-group label="Tuition Range" label-for="classTuitionRange" label-class="form-label">
        <div class="row">
          <b-form-group label="From" label-for="tuition_from" label-class="form-label" class="col-md-6">
            <currency-input
              id="tuition_from"
              v-model="uni.tuition_from"
              class="form-control"
              placeholder="Enter tuition fee range"
              :allow-negative="false"
              :currency="{ prefix: '$', suffix: '' }"
            />
          </b-form-group>
          <b-form-group label="To" label-for="tuition_to" label-class="form-label" class="col-md-6">
            <currency-input
              id="tuition_to"
              v-model="uni.tuition_to"
              class="form-control"
              placeholder="Enter tuition fee range"
              :allow-negative="false"
              :currency="{ prefix: '$', suffix: '' }"
            />
          </b-form-group>
        </div>
      </b-form-group>

      <b-form-group label="Pass Rate" label-for="passRatio" label-class="form-label">
        <currency-input
          id="passRatio"
          v-model="uni.pass_rate"
          class="form-control"
          placeholder="Enter pass percentage"
          :allow-negative="false"
          :value-range="{ min: 0, max: 100 }"
          :currency="{ prefix: '', suffix: '%' }"
        />
      </b-form-group>

      <b-form-group label="Average Program Weeks" label-for="avgProgramWeeks" label-class="form-label">
        <b-form-input
          id="avgProgramWeeks"
          v-model="uni.avg_prog_weeks"
          class="form-control"
          placeholder="Enter average program weeks"
          type="number"
          :allow-negative="false"
        />
      </b-form-group>

      <b-form-group label="Offerings" label-for="offerings" label-class="form-label">
        <multiselect 
          v-model="uni.offerings" 
          :options="offeringsOptions"
          :multiple="true" 
          class="choose-options"
          :group-values="groupValues" 
          :group-label="groupLabel" 
          placeholder="Select tags"
          track-by="value"
          label="text"
        />
      </b-form-group>
      
      
      <b-form-group :label="$t('schoolMsgs.offeredPrg')" label-for="offered_programs" label-class="form-label">
        <v-select
          id="offered_programs"
          class="form-control v-select-custom"
          label="title"
          v-model="uni.offered_programs"
          :reduce="offered_programs => offered_programs.id"
          placeholder="Select Offered Program"
          :options="allPrograms"
          multiple
          @input="handleOfferedProgramsChange"
          :loading="areProgramsLoading"
        >
          <template #search="{ attributes, events }">
            <input class="vs__search" v-bind="attributes" v-on="events" />
          </template>
          <template slot="option" slot-scope="option">
            <span>{{ option.title }}</span>
          </template>
        </v-select>
      </b-form-group>
      <b-form-group label="University Majors" label-for="university_majors" label-class="form-label">
        <v-select
          id="university_majors"
          class="form-control v-select-custom"
          label="text"
          v-model="uni.majors"
          :reduce="item => item.value"
          placeholder="All University Majors"
          :options="allUniMajors"
          multiple
        >
        </v-select>
      </b-form-group>
      <page-separator title="Programs Offered by University" />

      <div class="card mb-3" v-for="(pg, index) in uni.uni_offered" :key="index">
        <div class="card-header bg-light d-flex align-items-center">
          <h5 class="my-0">Program {{ index + 1 }}</h5>
          <div class="d-flex align-items-center ml-auto">
            <a
              href="#"
              class="btn btn-secondary btn-sm mr-2"
              :class="{ disabled: !pg.id }"
              @click.prevent="pg.id && openPrerequisitesModal(pg)"
              v-b-popover.hover.top="pg.id ? '' : 'Save the university first to manage prerequisites for this program.'"
            >
              Prerequisites
            </a>
            <a href="#" class="btn btn-primary btn-sm" @click.prevent="openChecklistConfigModal(pg.program)">
              University Checklist Config
            </a>
            <a
              href="#"
              class="btn btn-info ml-2 btn-sm"
              :class="{ disabled: !pg.id }"
              @click.prevent="pg.id && openAdmissionContactsModal(pg)"
              v-b-popover.hover.top="pg.id ? '' : 'Save the university first to manage admission contacts for this program.'"
            >
              Admission Contacts
            </a>
            <a
              href="#"
              class="btn btn-danger ml-2 btn-sm"
              @click.prevent="removeUniOfferedPg(index)"
              v-if="uni.uni_offered.length > 1"
            >
              <md-icon class="mr-2">close</md-icon>Remove
            </a>
          </div>
        </div>
        <div class="card-body">
          <b-row class="mx-0">
            <b-form-group label="Program" label-for="pg" label-class="form-label" class="col-md-6">
              <v-select
                :id="`pg-${index}`"
                class="form-control v-select-custom"
                label="title"
                v-model="pg.program"
                :reduce="offered_programs => offered_programs.id"
                placeholder="Select Program"
                :custom-label="customLabel"
                :options="degreePrograms"
                :loading="areProgramsLoading"
                @input="syncChecklistProgramsFromUniversityPrograms"
              >
                <template #search="{ attributes, events }">
                  <input class="vs__search" v-bind="attributes" v-on="events" />
                </template>
                <template slot="option" slot-scope="option">
                  <span>{{ option.title }}</span>
                </template>
              </v-select>

              <b-form-invalid-feedback>This field is required.</b-form-invalid-feedback>
            </b-form-group>
            <b-form-group label="Link" label-for="link" label-class="form-label" class="col-md-6">
              <b-form-input :id="`link-${index}`" placeholder="Enter Link" v-model="pg.enroll_link" />
            </b-form-group>
          </b-row>

          <b-form-group label="Description" label-for="description" label-class="form-label" class="col-md-12">
            <editor
              :init="{
                height: 400,
                menubar: 'edit view insert format tools table help',
                plugins: [
                  'advlist autolink lists link image charmap print preview anchor',
                  'searchreplace visualblocks code fullscreen',
                  'insertdatetime media table paste code help wordcount',
                ],
                toolbar:
                  'undo redo | formatselect | bold italic backcolor | \
                alignleft aligncenter alignright alignjustify | \
                bullist numlist outdent indent | removeformat | help',
              }"
              v-model="pg.description"
              placeholder="Enter university's program description"
            />
          </b-form-group>
          <page-separator title="How to get in" />
          <b-row class="mx-0">
            <b-form-group label="Title" label-for="how-to-title" label-class="form-label" class="col-md-6">
              <b-form-input id="how-to-title" placeholder="Enter how to get in video title" v-model="pg.how_to_title" />
            </b-form-group>
            <b-form-group label="Youtube Link" label-for="how-to-link" label-class="form-label" class="col-md-6">
              <b-form-input id="how-to-link" placeholder="Enter Link" v-model="pg.how_to_video" />
            </b-form-group>
          </b-row>
          <b-form-group label="Description" label-for="how-to-description" label-class="form-label" class="col-md-12">
            <textarea
              class="form-control"
              id="how-to-description"
              placeholder="Enter how to get in video description"
              v-model="pg.how_to_description"
            ></textarea>
          </b-form-group>
        </div>
      </div>

      <div class="d-flex justify-content-center mb-4">
        <a class="btn btn-light btn-sm" href="#" @click.prevent="addUniOfferedPg()">
          <md-icon class="mr-2">add</md-icon> Add More
        </a>
      </div>

      <b-modal
        v-model="isChecklistConfigModalOpen"
        title="University Checklist Config"
        size="xl"
        centered
        scrollable
        hide-footer
      >
        <div class="checklist-config-card">
          <div class="d-flex align-items-center justify-content-between flex-wrap mb-3">
            <h5 class="my-0">Checklist Configuration</h5>
            <div class="checklist-scope-picker">
              <v-select
                class="form-control v-select-custom mb-0"
                :options="checklistScopeOptions"
                label="text"
                :reduce="option => option.value"
                v-model="currentChecklistScope"
                placeholder="Select checklist scope"
                :clearable="false"
              />
            </div>
          </div>
          <p class="text-muted small mb-3">
            Configure title, points, and description for each checklist action in the selected scope. Toggle an item off to hide it from students.
          </p>

          <div v-if="!activeChecklistItems.length" class="text-muted text-center py-4">
            No checklist items available for this scope.
          </div>

          <div
            v-for="item in activeChecklistItems"
            :key="`${currentChecklistScope}-${item.abbr}`"
            class="checklist-item-row"
            :class="{ 'checklist-item-row--disabled': !item.is_enabled }"
          >
            <div class="checklist-item-row__header">
              <div class="d-flex align-items-center">
                <md-icon v-if="item.icon" class="checklist-item-row__icon mr-2">{{ item.icon }}</md-icon>
                <div class="font-weight-600">{{ getChecklistActionLabel(item.abbr) }}</div>
              </div>
              <b-form-checkbox switch v-model="item.is_enabled" class="mb-0">
                <span class="small text-muted">{{ item.is_enabled ? 'Enabled' : 'Disabled' }}</span>
              </b-form-checkbox>
            </div>

            <div class="row">
              <div class="col-md-7 mb-2 mb-md-0">
                <label class="form-label small text-muted mb-1">Title</label>
                <b-form-input v-model="item.title" placeholder="Enter title" :disabled="!item.is_enabled" />
              </div>
              <div class="col-md-2 mb-2 mb-md-0">
                <label class="form-label small text-muted mb-1">Points</label>
                <b-form-input
                  v-model.number="item.points"
                  type="number"
                  min="0"
                  :disabled="!item.is_enabled"
                />
              </div>
              <div class="col-md-3 mb-2 mb-md-0">
                <label class="form-label small text-muted mb-1">Button text</label>
                <b-form-input v-model="item.btn_text" placeholder="Complete" :disabled="!item.is_enabled" />
              </div>
              <div class="col-12 mt-2">
                <label class="form-label small text-muted mb-1">Description</label>
                <b-form-textarea
                  v-model="item.description"
                  placeholder="Enter description"
                  rows="2"
                  max-rows="4"
                  :disabled="!item.is_enabled"
                />
              </div>
              <div v-if="item.abbr === 'add_new_courses' && currentChecklistScope !== 'general'" class="col-12 mt-2">
                <label class="form-label small text-muted mb-1">Allowed Course Titles</label>
                <v-select
                  class="form-control v-select-custom mb-0"
                  :options="courseAllowedValueOptions"
                  multiple
                  taggable
                  push-tags
                  :close-on-select="false"
                  v-model="item.extra.allowed_values"
                  :disabled="!item.is_enabled"
                  placeholder="Select or type course titles"
                />
                <small class="form-text text-muted">
                  Students can only earn points for courses whose title matches one of these values. Leave empty to allow any course.
                </small>
              </div>
              <div v-if="item.abbr === 'add_certifications' && currentChecklistScope !== 'general'" class="col-12 mt-2">
                <label class="form-label small text-muted mb-1">Allowed Certificate Programs</label>
                <v-select
                  class="form-control v-select-custom mb-0"
                  :options="certificatePrograms"
                  label="title"
                  :reduce="program => program.id"
                  multiple
                  :close-on-select="false"
                  v-model="item.extra.allowed_values"
                  :disabled="!item.is_enabled"
                  :loading="areProgramsLoading"
                  placeholder="Select certificate programs"
                />
                <small class="form-text text-muted">
                  Students can only earn points for certifications that map to one of these certificate programs. Leave empty to allow any program.
                </small>
              </div>
              <div v-if="item.abbr === 'add_prep_courses' && currentChecklistScope !== 'general'" class="col-12 mt-2">
                <label class="form-label small text-muted mb-1">Allowed Prep Courses</label>
                <v-select
                  class="form-control v-select-custom mb-0"
                  :options="prepCoursePrograms"
                  label="title"
                  :reduce="program => program.id"
                  multiple
                  :close-on-select="false"
                  v-model="item.extra.allowed_values"
                  :disabled="!item.is_enabled"
                  :loading="areProgramsLoading"
                  placeholder="Select prep courses"
                />
                <small class="form-text text-muted">
                  Students can only earn points for prep courses that map to one of these. Leave empty to allow any.
                </small>
              </div>
              <div v-if="item.abbr === 'add_admissions_assessments' && currentChecklistScope !== 'general'" class="col-12 mt-2">
                <label class="form-label small text-muted mb-1">Allowed Assessments</label>
                <v-select
                  class="form-control v-select-custom mb-0"
                  :options="admissionsAssessmentOptions"
                  multiple
                  taggable
                  push-tags
                  :close-on-select="false"
                  v-model="item.extra.allowed_values"
                  :disabled="!item.is_enabled"
                  placeholder="Select or type assessments (e.g. TEAS, HESI A2)"
                />
                <small class="form-text text-muted">
                  Students can only earn points for assessments whose name matches one of these. Leave empty to allow any.
                </small>
              </div>
            </div>
          </div>
        </div>
      </b-modal>

      <b-modal
        v-model="isPrerequisitesModalOpen"
        :title="`Prerequisites${prerequisitesProgramTitle ? ' - ' + prerequisitesProgramTitle : ''}`"
        size="xl"
        centered
        scrollable
        hide-footer
      >
        <div v-if="isPrerequisitesLoading" class="text-center py-4">
          <i class="fas fa-circle-notch fa-spin"></i>
        </div>

        <div v-else>
          <p class="text-muted small">
            What a student needs before applying to this program at this school. Shown to students above the program
            checklist.
          </p>
          <div v-if="!prerequisites.length" class="text-muted text-center py-3">No prerequisites yet.</div>

          <div v-for="(prereq, idx) in prerequisites" :key="prereq.id || `new-prereq-${idx}`" class="checklist-item-row">
            <div class="checklist-item-row__header">
              <div class="font-weight-600">{{ prereq.name || 'New Prerequisite' }}</div>
              <a href="#" class="text-danger small" @click.prevent="removePrerequisite(idx)">
                <i class="fas fa-trash mr-1"></i>Remove
              </a>
            </div>

            <div class="row">
              <div class="col-md-3 mb-2">
                <label class="form-label small text-muted mb-1">Type</label>
                <v-select
                  class="form-control v-select-custom mb-0"
                  :options="prerequisiteTypeOptions"
                  label="text"
                  :reduce="option => option.value"
                  v-model="prereq.type"
                  :clearable="false"
                  placeholder="Select type"
                />
              </div>
              <div class="col-md-6 mb-2">
                <label class="form-label small text-muted mb-1">Name</label>
                <b-form-input v-model="prereq.name" placeholder="e.g. Anatomy & Physiology I + Lab" />
              </div>
              <div class="col-md-3 mb-2">
                <label class="form-label small text-muted mb-1">Level</label>
                <v-select
                  class="form-control v-select-custom mb-0"
                  :options="prerequisiteLevelOptions"
                  label="text"
                  :reduce="option => option.value"
                  v-model="prereq.level"
                  :clearable="false"
                />
              </div>
            </div>

            <div class="row">
              <template v-if="prereq.type === 'college_course'">
                <div class="col-md-2 mb-2">
                  <label class="form-label small text-muted mb-1">Min Grade</label>
                  <b-form-input v-model="prereq.min_grade" placeholder="C" />
                </div>
                <div class="col-md-2 mb-2">
                  <label class="form-label small text-muted mb-1">Credits</label>
                  <b-form-input v-model="prereq.credits" type="number" step="0.5" min="0" />
                </div>
                <div class="col-md-2 mb-2">
                  <label class="form-label small text-muted mb-1">Has Lab</label>
                  <b-form-select v-model="prereq.has_lab" :options="yesNoUnknownOptions" />
                </div>
              </template>
              <template v-else-if="['entrance_exam', 'certification', 'prep_course'].includes(prereq.type)">
                <div class="col-md-2 mb-2">
                  <label class="form-label small text-muted mb-1">Min Score</label>
                  <b-form-input v-model="prereq.min_score" type="number" step="any" min="0" />
                </div>
                <div class="col-md-4 mb-2">
                  <label class="form-label small text-muted mb-1">Score Label</label>
                  <b-form-input v-model="prereq.score_label" placeholder="e.g. ATI TEAS composite %" />
                </div>
              </template>
              <div class="col-md-2 mb-2">
                <label class="form-label small text-muted mb-1">Within (years)</label>
                <b-form-input v-model="prereq.recency_years" type="number" min="0" placeholder="Any" />
              </div>
              <div class="col-md-4 mb-2">
                <label class="form-label small text-muted mb-1">Due By</label>
                <b-form-select v-model="prereq.due_by" :options="prerequisiteDueByOptions" />
              </div>
            </div>

            <div class="row" v-if="prereq.type === 'certification'">
              <div class="col-md-6 mb-2">
                <label class="form-label small text-muted mb-1">Certificate Program (optional)</label>
                <v-select
                  class="form-control v-select-custom mb-0"
                  :options="certificatePrograms"
                  label="title"
                  :reduce="program => program.id"
                  v-model="prereq.certification_program"
                  :loading="areProgramsLoading"
                  placeholder="Link to a certificate program, e.g. CNA"
                />
              </div>
            </div>

            <div class="row" v-if="prereq.type === 'college_course'">
              <div class="col-md-3 mb-2">
                <label class="form-label small text-muted mb-1">Accepts ACE</label>
                <b-form-select v-model="prereq.accepts_ace" :options="yesNoUnknownOptions" />
              </div>
              <div class="col-md-3 mb-2">
                <label class="form-label small text-muted mb-1">Accepts CLEP</label>
                <b-form-select v-model="prereq.accepts_clep" :options="yesNoUnknownOptions" />
              </div>
              <div class="col-md-3 mb-2">
                <label class="form-label small text-muted mb-1">Accepts DSST</label>
                <b-form-select v-model="prereq.accepts_dsst" :options="yesNoUnknownOptions" />
              </div>
              <div class="col-md-3 mb-2">
                <label class="form-label small text-muted mb-1">Accepts Online Lab</label>
                <b-form-select v-model="prereq.accepts_online_lab" :options="yesNoUnknownOptions" />
              </div>
            </div>

            <div class="row">
              <div class="col-md-3 mb-2">
                <label class="form-label small text-muted mb-1">School Admission Points</label>
                <b-form-input v-model="prereq.points" type="number" min="0" placeholder="None" />
              </div>
              <div class="col-md-3 mb-2">
                <label class="form-label small text-muted mb-1">Choice Group</label>
                <b-form-input v-model="prereq.choice_group" placeholder="e.g. math" />
              </div>
              <div class="col-md-4 mb-2">
                <label class="form-label small text-muted mb-1">Source URL</label>
                <b-form-input v-model="prereq.source_url" placeholder="Program admissions page" />
              </div>
              <div class="col-md-2 mb-2">
                <label class="form-label small text-muted mb-1">Order</label>
                <b-form-input v-model="prereq.display_order" type="number" min="0" />
              </div>
              <div class="col-12">
                <small class="form-text text-muted mt-0 mb-2">
                  School Admission Points are points on the school's own admission rubric (for example CNA = 5),
                  not TransitionU checklist points. Items with the same Choice Group are alternatives.
                </small>
              </div>
              <div class="col-12">
                <label class="form-label small text-muted mb-1">Notes</label>
                <b-form-textarea v-model="prereq.notes" placeholder="Shown to students" rows="2" max-rows="4" />
              </div>
            </div>

            <div class="d-flex justify-content-end align-items-center mt-2">
              <small class="text-muted mr-3" v-if="prereq.verified_at">
                Last verified {{ formatVerifiedDate(prereq.verified_at) }}
              </small>
              <b-btn variant="primary" size="sm" :disabled="prereq.isSaving" @click="savePrerequisite(idx)">
                <i v-if="prereq.isSaving" class="fas fa-circle-notch fa-spin mr-1"></i>
                {{ prereq.id ? 'Update' : 'Save' }}
              </b-btn>
            </div>
          </div>

          <div class="d-flex justify-content-center mt-3">
            <a class="btn btn-light btn-sm" href="#" @click.prevent="addPrerequisiteRow()">
              <md-icon class="mr-2">add</md-icon> Add Prerequisite
            </a>
          </div>
        </div>
      </b-modal>

      <b-modal
        v-model="isAdmissionContactsModalOpen"
        :title="`Admission Contacts${admissionContactsProgramTitle ? ' - ' + admissionContactsProgramTitle : ''}`"
        size="xl"
        centered
        scrollable
        hide-footer
      >
        <div v-if="isAdmissionContactsLoading" class="text-center py-4">
          <i class="fas fa-circle-notch fa-spin"></i>
        </div>

        <div v-else>
          <div v-if="!admissionContacts.length" class="text-muted text-center py-3">
            No admission contacts yet.
          </div>

          <div
            v-for="(contact, idx) in admissionContacts"
            :key="contact.id || `new-${idx}`"
            class="checklist-item-row"
          >
            <div class="checklist-item-row__header">
              <div class="font-weight-600">
                {{ contact.name || 'New Contact' }}
              </div>
              <a
                href="#"
                class="text-danger small"
                @click.prevent="removeAdmissionContact(idx)"
              >
                <i class="fas fa-trash mr-1"></i>Remove
              </a>
            </div>

            <div class="row">
              <div class="col-md-6 mb-2">
                <label class="form-label small text-muted mb-1">Type</label>
                <v-select
                  class="form-control v-select-custom mb-0"
                  :options="admissionContactTypeOptions"
                  label="text"
                  :reduce="option => option.value"
                  v-model="contact.type"
                  placeholder="Select type"
                />
              </div>
              <div class="col-md-6 mb-2" v-if="contact.type === 'Other'">
                <label class="form-label small text-muted mb-1">Other Type</label>
                <b-form-input v-model="contact.type_other" placeholder="Enter type" />
              </div>
              <div class="col-md-6 mb-2">
                <label class="form-label small text-muted mb-1">Name</label>
                <b-form-input v-model="contact.name" placeholder="Enter name" />
              </div>
              <div class="col-md-6 mb-2">
                <label class="form-label small text-muted mb-1">Title</label>
                <b-form-input v-model="contact.title" placeholder="Enter title" />
              </div>
              <div class="col-md-6 mb-2">
                <label class="form-label small text-muted mb-1">Email</label>
                <b-form-input v-model="contact.email" type="email" placeholder="Enter email" />
              </div>
              <div class="col-md-6 mb-2">
                <label class="form-label small text-muted mb-1">Phone</label>
                <b-form-input v-model="contact.phone" placeholder="Enter phone" />
              </div>
              <div class="col-12 mt-2">
                <label class="form-label small text-muted mb-1">Notes</label>
                <b-form-textarea v-model="contact.notes" placeholder="Enter notes" rows="2" max-rows="4" />
              </div>
            </div>

            <div class="d-flex justify-content-end mt-2">
              <b-btn
                variant="primary"
                size="sm"
                :disabled="contact.isSaving"
                @click="saveAdmissionContact(idx)"
              >
                <i v-if="contact.isSaving" class="fas fa-circle-notch fa-spin mr-1"></i>
                {{ contact.id ? 'Update' : 'Save' }}
              </b-btn>
            </div>
          </div>

          <div class="d-flex justify-content-center mt-3">
            <a class="btn btn-light btn-sm" href="#" @click.prevent="addAdmissionContactRow()">
              <md-icon class="mr-2">add</md-icon> Add Contact
            </a>
          </div>
        </div>
      </b-modal>

      <b-btn variant="primary" :disabled="isFormLoading" style="width: 150px" type="submit" class="btn-normal">
        <i v-if="isLoading" class="fas fa-circle-notch fa-spin"></i>
        <span v-else>{{ $route.params.id ? $t('update') : $t('add') }}</span>
      </b-btn>
    </b-form>
  </div>
</template>

<script>
import axios from 'axios';
import hexRgb from 'hex-rgb';
import { countries } from 'countries-list';
import { get, map, intersection } from 'lodash';
import { mapActions } from 'vuex';
import { FmvAvatar } from 'fmv-avatar';
import PageHeader from '@/components/Ui/PageHeader.vue';
import PageSeparator from '@/components/Ui/PageSeparator.vue';
import Editor from '@tinymce/tinymce-vue';
import Multiselect from "vue-multiselect";

import Page from '../../../components/Page.vue';
import {
  PROFILE_VISIBILITY_OPTIONS,
  INSTITUTION_TYPE_OPTIONS,
  CNA_PAYMENT_PLANS,
  UNI_OFFERINGS,
  COURSE_ALLOWED_VALUE_OPTIONS,
} from '../../../common/constants';
import { getClickableLink } from '@/common/utils';
import VSwatches from 'vue-swatches';
// import UserAvatar from '../../../components/User/UserAvatar.vue';
import 'vue-swatches/dist/vue-swatches.css';
import { constants } from '../../../common/constants';
var UsaStates = require('usa-states').UsaStates;
import ImageUploader from '@/components/ImageUploader.vue';

export default {
  components: {
    PageHeader,
    PageSeparator,
    FmvAvatar,
    VSwatches,
    Editor,
    ImageUploader,
    Multiselect,
  },
  extends: Page,

  data() {
    return {
      PROFILE_VISIBILITY_OPTIONS,
      INSTITUTION_TYPE_OPTIONS,
      CNA_PAYMENT_PLANS,
      tinemceApiKey: process.env.VUE_APP_TINEMCE_API_KEY,
      swatches: [
        ['#1FBC9C', '#1CA085', '#2ECC70', '#3398DB'],
        ['#3B86F5', '#8E43AD', '#3D556E', '#222F3D'],
        ['#181818', '#F39C19', '#E84B3C', '#C0382B'],
        ['#DDE6E8', '#BDC3C8', '#C0382B', '#2F6FED'],
      ],
      title: this.$route.params.id ? 'Edit University' : 'Add University',
      selectedTags: [],
      isPictureLoading: false,
      photo_gallery: [],
      allUniMajors: [
        { value: 'General', text: 'General' },
        { value: 'Agriculture and Environmental Science', text: 'Agriculture and Environmental Science' },
        { value: 'Architecture and Urban Planning', text: 'Architecture and Urban Planning' },
        { value: 'Arts and Humanities', text: 'Arts and Humanities' },
        { value: 'Business and Economics', text: 'Business and Economics' },
        { value: 'Engineering and Technology', text: 'Engineering and Technology' },
        { value: 'Health Sciences', text: 'Health Sciences' },
        { value: 'Social Sciences and Education', text: 'Social Sciences and Education' },
        { value: 'Communication and Media Studies', text: 'Communication and Media Studies' },
        { value: 'Vocational and Technical Training', text: 'Vocational and Technical Training' },
        { value: 'Hospitality and Culinary Arts', text: 'Hospitality and Culinary Arts' },
        { value: 'Law and Public Policy', text: 'Law and Public Policy' },
        { value: 'Interdisciplinary Studies', text: 'Interdisciplinary Studies' },
      ],
      uni: {
        name: '',
        contact_email: '',
        phone: '',
        zipcode: '',
        address: '',
        city: '',
        state: '',
        subdomain: '',
        country: 'United States',
        logoUrl: null,
        small_logo_url: null,
        profileBgImgUrl: null,
        primaryColor: '#3B86F5',
        secondaryColor: '#2F6FED',
        darkColor: '#181818',
        is_profile_visible: true,
        short_bio: '',
        offerings: [],
        otherOfferings: null,

        offered_programs: [],
        scholarships: [],
        avg_prog_weeks: null,
        tuition_to: null,
        tuition_from: null,
        pass_rate: null,
        website: null,
        institution_type: null,
        uni_offered: [
          {
            program: null,
            enroll_link: '',
            description: '',
            how_to_title: '',
            how_to_link: '',
            how_to_desc: '',
          },
        ],
        majors: [],
      },

      countryOptions: [],
      stateOptions: [],
      offeringsOptions: UNI_OFFERINGS,
      groupValues: "options",
      groupLabel: "label",
      customLabel(option) {
        return `${option.text}`;
      },
      passErrorCode: null,
      isPassCopied: false,
      prevLength: null,
      isLoading: false,
      isLogoUploading: false,
      isProfileBgImgUploading: false,
      isSmallLogoUploading: false,
      areSchLoading: false,
      allScholarships: [],
      uploadFile: {
        logo: null,
        profileBgImage: null,
        smallLogo: null,
      },
      uploadPercent: { logo: 0, profileBgImage: 0, smallLogo: 0 },
      uploadCancelTokenSource: {
        logo: null,
        profileBgImage: null,
        smallLogo: null,
      },
      allPrograms: [],
      degreePrograms: [],
      certificatePrograms: [],
      prepCoursePrograms: [],
      admissionsAssessmentOptions: ['TEAS', 'HESI A2', 'Casper', 'PAX-RN', 'Kaplan Nursing'],
      areProgramsLoading: false,
      communityLink: '',
      checklistActionDefaults: [
        {
          abbr: 'career_assessment',
          title: 'Take a Career Assessment',
          description: 'Complete a Basic Assessment to earn 1 point or an Advanced Assessment to earn 5.',
          points: 1,
          btn_text: 'Complete',
          icon: 'assessment',
          redirect_to: '',
          display_order: 1,
          is_enabled: true,
        },
        {
          abbr: 'education_resume',
          title: 'Add Education to Resume',
          description: 'Earn 1 point for every education entry added to your resume.',
          points: 1,
          btn_text: 'Complete',
          icon: 'description',
          redirect_to: 'std-manage-resume',
          display_order: 2,
          is_enabled: true,
        },
        {
          abbr: 'work_experience_resume',
          title: 'Add Work Experience to Resume',
          description: 'Earn 1 point for each work experience entry you include in your resume.',
          points: 1,
          btn_text: 'Complete',
          icon: 'description',
          redirect_to: 'std-manage-resume',
          display_order: 3,
          is_enabled: true,
        },
        {
          abbr: 'volunteer_experience_resume',
          title: 'Add Volunteer Work Experience to Resume',
          description: 'Earn 1 point for each volunteer work experience entry you include in your resume.',
          points: 1,
          btn_text: 'Complete',
          icon: 'description',
          redirect_to: 'std-manage-resume',
          display_order: 4,
          is_enabled: true,
        },
        {
          abbr: 'skills_resume',
          title: 'Add Skills to Resume',
          description: 'Earn 1 point for each skill entry to your resume.',
          points: 1,
          btn_text: 'Complete',
          icon: 'description',
          redirect_to: 'std-manage-resume',
          display_order: 5,
          is_enabled: true,
        },
        {
          abbr: 'latest_title_location_resume',
          title: 'Add Latest Title and Location to Resume',
          description: 'Earn up to 2 points for adding your current job title and location.',
          points: 2,
          btn_text: 'Complete',
          icon: 'description',
          redirect_to: 'std-manage-resume',
          display_order: 6,
          is_enabled: true,
        },
        {
          abbr: 'practice_skill_simulations',
          title: 'Interview',
          description: 'Earn 3 points for completing a real-world skill interview.',
          points: 3,
          btn_text: 'Complete',
          icon: 'mic',
          redirect_to: 'student-simulations-listing',
          display_order: 7,
          is_enabled: true,
        },
        {
          abbr: 'add_certifications',
          title: 'Add Certifications',
          description: 'Earn 5 points for each certification you upload to your profile.',
          points: 5,
          btn_text: 'Complete',
          icon: 'assignment_turned_in',
          redirect_to: 'add-certificate',
          display_order: 8,
          is_enabled: true,
        },
        {
          abbr: 'add_new_courses',
          title: 'Add Courses',
          description: 'Earn 3 points for every course entry you include to demonstrate academic progress.',
          points: 3,
          btn_text: 'Complete',
          icon: 'local_library',
          redirect_to: 'add-course',
          display_order: 9,
          is_enabled: true,
        },
        {
          abbr: 'add_letters_of_recommendation',
          title: 'Add Letters of Recommendation',
          description: 'Earn 5 points for each letter of recommendation you add to your profile.',
          points: 5,
          btn_text: 'Complete',
          icon: 'mail',
          redirect_to: 'add-letter-of-recommendation',
          display_order: 10,
          is_enabled: true,
        },
        {
          abbr: 'add_activities',
          title: 'Add Activities',
          description: 'Earn 2 points for each activity (clubs, leadership, volunteering) you add.',
          points: 2,
          btn_text: 'Complete',
          icon: 'emoji_events',
          redirect_to: 'add-activity',
          display_order: 11,
          is_enabled: true,
        },
        {
          abbr: 'add_admissions_assessments',
          title: 'Add Admissions Assessments',
          description: 'Earn 5 points for each admissions assessment (e.g. TEAS, HESI, Casper) you add.',
          points: 5,
          btn_text: 'Complete',
          icon: 'assignment',
          redirect_to: 'add-admissions-assessment',
          display_order: 12,
          is_enabled: true,
        },
        {
          abbr: 'add_licenses',
          title: 'Add Licenses',
          description: 'Earn 5 points for each professional license you add to your profile.',
          points: 5,
          btn_text: 'Complete',
          icon: 'badge',
          redirect_to: 'add-license',
          display_order: 13,
          is_enabled: true,
        },
        {
          abbr: 'add_degrees',
          title: 'Add Degrees',
          description: 'Earn 5 points for each degree you add to your profile.',
          points: 5,
          btn_text: 'Complete',
          icon: 'school',
          redirect_to: 'add-degree',
          display_order: 14,
          is_enabled: true,
        },
        {
          abbr: 'add_prep_courses',
          title: 'Add Prep Courses',
          description: 'Earn 3 points for each prep course you complete and add to your profile.',
          points: 3,
          btn_text: 'Complete',
          icon: 'menu_book',
          redirect_to: 'add-prep-course',
          display_order: 15,
          is_enabled: true,
        },
      ],
      checklistGeneral: [],
      checklistPrograms: [],
      currentChecklistScope: 'general',
      isChecklistConfigModalOpen: false,
      courseAllowedValueOptions: COURSE_ALLOWED_VALUE_OPTIONS,

      isPrerequisitesModalOpen: false,
      isPrerequisitesLoading: false,
      prerequisitesUniOfferedProgramId: null,
      prerequisitesProgramTitle: '',
      prerequisites: [],
      prerequisiteTypeOptions: [
        { value: 'college_course', text: 'College course' },
        { value: 'entrance_exam', text: 'Entrance exam' },
        { value: 'certification', text: 'Certification' },
        { value: 'prep_course', text: 'Prep course' },
        { value: 'compliance', text: 'Compliance' },
        { value: 'experience', text: 'Experience' },
        { value: 'general', text: 'General' },
      ],
      prerequisiteLevelOptions: [
        { value: 'required', text: 'Required' },
        { value: 'highly_recommended', text: 'Highly recommended' },
        { value: 'recommended', text: 'Recommended' },
      ],
      prerequisiteDueByOptions: [
        { value: null, text: 'Not specified' },
        { value: 'application', text: 'By the application deadline' },
        { value: 'program_start', text: 'Before the program starts' },
        { value: 'clinicals', text: 'Before clinicals' },
      ],
      yesNoUnknownOptions: [
        { value: null, text: 'Unknown' },
        { value: true, text: 'Yes' },
        { value: false, text: 'No' },
      ],
      isAdmissionContactsModalOpen: false,
      isAdmissionContactsLoading: false,
      admissionContactsUniOfferedProgramId: null,
      admissionContactsProgramTitle: '',
      admissionContacts: [],
      admissionContactTypeOptions: [
        { value: 'Admissions Officer', text: 'Admissions Officer' },
        { value: 'Faculty', text: 'Faculty' },
        { value: 'Alumni', text: 'Alumni' },
        { value: 'Current Student', text: 'Current Student' },
        { value: 'Advisor', text: 'Advisor' },
        { value: 'Other', text: 'Other' },
      ],
    };
  },

  computed: {
    breadcrumb() {
      return [
        { text: this.$t('home'), to: this.routes.home },
        { text: 'Manage Universities', to: { name: 'manage-unis' } },
        {
          text: this.$route.params.id ? 'Edit University' : 'Add University',
          active: true,
        },
      ];
    },
    isFormLoading() {
      return this.isLoading || this.isLogoUploading || this.isProfileBgImgUploading || this.isSmallLogoUploading;
    },
    checklistScopeOptions() {
      const options = [{ value: 'general', text: 'General Checklist' }];

      this.getUniversityOfferedProgramIds().forEach(programId => {
        options.push({
          value: `program-${programId}`,
          text: this.getProgramTitle(programId),
        });
      });

      return options;
    },
    activeChecklistItems() {
      if (this.currentChecklistScope === 'general') {
        return this.checklistGeneral;
      }

      const programId = Number(String(this.currentChecklistScope || '').replace('program-', ''));
      if (!programId) {
        return this.checklistGeneral;
      }

      return this.ensureProgramChecklistGroup(programId).checklist;
    },
  },
  // validations() {
  //   return {
  //     cnaAccDetails: {
  //       sendgrid_api_key: { required: requiredIf(() => this.cnaAccDetails.sendgrid_from_email) },
  //       sendgrid_from_email: { required: requiredIf(() => this.cnaAccDetails.sendgrid_api_key), email },
  //       paymentPlan: { required: requiredIf(() => this.cnaAcc || this.cnaAccRes) },
  //     },
  //   };
  // },
  methods: {
    ...mapActions('uni', [
      'getUni',
      'createUni',
      'updateUni',
      'getUniImageUploadPresignedUrl',
      'getAllAdmissionContacts',
      'createAdmissionContact',
      'updateAdmissionContact',
      'deleteAdmissionContact',
      'getAllPrerequisites',
      'createPrerequisite',
      'updatePrerequisite',
      'deletePrerequisite',
    ]),
    ...mapActions('s3Upload', ['uploadToPresignedUrl']),
    ...mapActions('program', ['getAllPrograms']),
    ...mapActions('scholarships', ['getAllNewScholarships']),
    get,
    getClickableLink,
    setImage(file, image) {
      this.uploadFile[image] = file;
      this.uploadImage(image);
    },
    getChecklistActionDefaults(abbr) {
      return (this.checklistActionDefaults || []).find(action => action.abbr === abbr) || null;
    },
    getUniversityOfferedProgramIds() {
      const ids = (this.uni.uni_offered || []).map(item => Number(item.program)).filter(Boolean);
      return [...new Set(ids)];
    },
    getProgramTitle(programId) {
      const program = (this.allPrograms || []).find(item => Number(item.id) === Number(programId));
      return program ? `${program.title} Checklist` : `Program ${programId} Checklist`;
    },
    getChecklistActionLabel(abbr) {
      return this.getChecklistActionDefaults(abbr)?.title || '';
    },
    getDefaultChecklistTemplate() {
      return (this.checklistActionDefaults || []).map(action => this.createDefaultChecklistItem(action.abbr));
    },
    mergeChecklistWithDefaults(items = []) {
      const normalizedItems = (items || []).map(item => this.normalizeChecklistItem(item));
      const normalizedByAbbr = normalizedItems.reduce((acc, item) => {
        if (item.abbr) {
          acc[item.abbr] = item;
        }
        return acc;
      }, {});

      const merged = (this.checklistActionDefaults || []).map(action =>
        this.normalizeChecklistItem(normalizedByAbbr[action.abbr] || { abbr: action.abbr }),
      );

      const extra = normalizedItems.filter(item => item.abbr && !this.getChecklistActionDefaults(item.abbr));
      return [...merged, ...extra];
    },
    ensureProgramChecklistGroup(programId) {
      let group = (this.checklistPrograms || []).find(item => Number(item.program) === Number(programId));
      if (!group) {
        group = {
          program: programId,
          checklist: this.getDefaultChecklistTemplate(),
        };
        this.checklistPrograms.push(group);
      }
      return group;
    },
    syncChecklistProgramsFromUniversityPrograms() {
      const offeredPrograms = this.getUniversityOfferedProgramIds();
      const existingGroups = this.checklistPrograms || [];

      this.checklistPrograms = existingGroups.filter(group => offeredPrograms.includes(Number(group.program)));

      const validScope =
        this.currentChecklistScope === 'general' ||
        offeredPrograms.some(programId => this.currentChecklistScope === `program-${programId}`);

      if (!validScope) {
        this.currentChecklistScope = 'general';
      }
    },
    openChecklistConfigModal(programId) {
      const normalizedProgramId = Number(programId);
      if (!normalizedProgramId) {
        this.makeToast({ variant: 'warning', msg: 'Please select a program first to configure its checklist.' });
        return;
      }
      this.ensureProgramChecklistGroup(normalizedProgramId);
      this.currentChecklistScope = `program-${normalizedProgramId}`;
      this.isChecklistConfigModalOpen = true;
    },
    async handleOfferedProgramsChange() {
      await this.getScholarships();
    },
    createDefaultChecklistItem() {
      const abbr = arguments.length ? arguments[0] : '';
      const defaults = this.getChecklistActionDefaults(abbr) || {};
      return {
        abbr: defaults.abbr || abbr || '',
        title: defaults.title || '',
        description: defaults.description || '',
        points: Number.isFinite(Number(defaults.points)) ? Number(defaults.points) : 0,
        is_enabled: defaults.is_enabled !== false,
        btn_text: defaults.btn_text || 'Complete',
        icon: defaults.icon || '',
        redirect_to: defaults.redirect_to || '',
        display_order: Number.isFinite(Number(defaults.display_order)) ? Number(defaults.display_order) : 0,
        extra: { allowed_values: [] },
      };
    },
    normalizeAllowedValues(values) {
      if (!Array.isArray(values)) return [];
      return values
        .map(v => {
          if (typeof v === 'string') return v.trim();
          if (typeof v === 'number' && Number.isFinite(v)) return v;
          return null;
        })
        .filter(v => v !== null && v !== '');
    },
    normalizeChecklistItem(item = {}) {
      const defaults = this.getChecklistActionDefaults(item.abbr) || {};
      const extraIn = item.extra && typeof item.extra === 'object' ? item.extra : {};
      const allowedValues = this.normalizeAllowedValues(extraIn.allowed_values);
      return {
        abbr: item.abbr || defaults.abbr || '',
        title: item.title || defaults.title || '',
        description: item.description || defaults.description || '',
        points: Number.isFinite(Number(item.points)) ? Number(item.points) : Number(defaults.points || 0),
        is_enabled: typeof item.is_enabled === 'boolean' ? item.is_enabled : defaults.is_enabled !== false,
        btn_text: item.btn_text || defaults.btn_text || 'Complete',
        icon: item.icon || defaults.icon || '',
        redirect_to: item.redirect_to || defaults.redirect_to || '',
        display_order: Number.isFinite(Number(item.display_order))
          ? Number(item.display_order)
          : Number(defaults.display_order || 0),
        extra: { ...extraIn, allowed_values: allowedValues },
      };
    },
    getChecklistGeneralPayload() {
      return (this.checklistGeneral || [])
        .map(item => {
          const normalized = this.normalizeChecklistItem(item);
          // General checklist does not use extra.allowed_values; strip it.
          delete normalized.extra;
          return normalized;
        })
        .filter(item => item.abbr);
    },
    getChecklistProgramsPayload() {
      return (this.checklistPrograms || [])
        .map(group => ({
          program: group.program,
          checklist: (group.checklist || []).map(item => this.normalizeChecklistItem(item)).filter(item => item.abbr),
        }))
        .filter(group => group.program && group.checklist.length);
    },
    async openPrerequisitesModal(pg) {
      if (!pg || !pg.id) return;
      this.prerequisitesUniOfferedProgramId = pg.id;
      this.prerequisitesProgramTitle = this.getProgramTitle(pg.program).replace(' Checklist', '');
      this.isPrerequisitesModalOpen = true;
      await this.fetchPrerequisites();
    },
    async fetchPrerequisites() {
      this.isPrerequisitesLoading = true;
      try {
        const res = await this.getAllPrerequisites({
          uni_offered_program: this.prerequisitesUniOfferedProgramId,
          ordering: 'display_order',
          limit: 200,
        });
        const results = (res.data && res.data.results) || res.results || res.data || [];
        this.prerequisites = results.map(item => ({ ...item, isSaving: false }));
      } catch (e) {
        this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
      }
      this.isPrerequisitesLoading = false;
    },
    addPrerequisiteRow() {
      this.prerequisites.push({
        id: null,
        uni_offered_program: this.prerequisitesUniOfferedProgramId,
        type: 'college_course',
        name: '',
        level: 'required',
        certification_program: null,
        min_grade: '',
        min_score: null,
        score_label: '',
        points: null,
        credits: null,
        has_lab: null,
        recency_years: null,
        due_by: null,
        accepts_clep: null,
        accepts_ace: null,
        accepts_dsst: null,
        accepts_online_lab: null,
        choice_group: '',
        notes: '',
        source_url: '',
        display_order: this.prerequisites.length,
        isSaving: false,
      });
    },
    formatVerifiedDate(value) {
      const date = value ? new Date(value) : null;
      if (!date || isNaN(date.getTime())) return '';
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    },
    async savePrerequisite(idx) {
      const prereq = this.prerequisites[idx];
      if (!prereq) return;
      if (!prereq.type || !(prereq.name || '').trim()) {
        this.makeToast({ variant: 'warning', msg: 'Type and name are required.' });
        return;
      }
      const num = value => (value === '' || value === null || value === undefined ? null : Number(value));
      const payload = {
        uni_offered_program: this.prerequisitesUniOfferedProgramId,
        type: prereq.type,
        name: prereq.name.trim(),
        level: prereq.level || 'required',
        certification_program: prereq.type === 'certification' ? prereq.certification_program || null : null,
        min_grade: prereq.min_grade || null,
        min_score: num(prereq.min_score),
        score_label: prereq.score_label || null,
        points: num(prereq.points),
        credits: num(prereq.credits),
        has_lab: prereq.has_lab,
        recency_years: num(prereq.recency_years),
        due_by: prereq.due_by || null,
        accepts_clep: prereq.accepts_clep,
        accepts_ace: prereq.accepts_ace,
        accepts_dsst: prereq.accepts_dsst,
        accepts_online_lab: prereq.accepts_online_lab,
        choice_group: prereq.choice_group || '',
        notes: prereq.notes || null,
        source_url: prereq.source_url || null,
        verified_at: new Date().toISOString(),
        display_order: num(prereq.display_order) || 0,
      };
      this.$set(this.prerequisites[idx], 'isSaving', true);
      try {
        if (prereq.id) {
          await this.updatePrerequisite({ id: prereq.id, data: payload });
          this.makeToast({ variant: 'success', msg: 'Prerequisite updated.' });
        } else {
          const resp = await this.createPrerequisite(payload);
          const created = (resp.data && resp.data.data) || resp.data || {};
          this.$set(this.prerequisites[idx], 'id', created.id || null);
          this.makeToast({ variant: 'success', msg: 'Prerequisite added.' });
        }
        this.$set(this.prerequisites[idx], 'verified_at', payload.verified_at);
      } catch (e) {
        this.makeToast({
          variant: 'danger',
          msg: get(e, 'response.data.message') || this.$t('generalMsgs.genErrorMsg'),
        });
      }
      this.$set(this.prerequisites[idx], 'isSaving', false);
    },
    async removePrerequisite(idx) {
      const prereq = this.prerequisites[idx];
      if (!prereq) return;
      if (!prereq.id) {
        this.prerequisites.splice(idx, 1);
        return;
      }
      try {
        await this.deletePrerequisite(prereq.id);
        this.prerequisites.splice(idx, 1);
        this.makeToast({ variant: 'success', msg: 'Prerequisite removed.' });
      } catch (e) {
        this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
      }
    },
    async openAdmissionContactsModal(pg) {
      if (!pg || !pg.id) return;
      this.admissionContactsUniOfferedProgramId = pg.id;
      this.admissionContactsProgramTitle = this.getProgramTitle(pg.program).replace(' Checklist', '');
      this.isAdmissionContactsModalOpen = true;
      await this.fetchAdmissionContacts();
    },
    async fetchAdmissionContacts() {
      this.isAdmissionContactsLoading = true;
      try {
        const res = await this.getAllAdmissionContacts({
          uni_offered_program: this.admissionContactsUniOfferedProgramId,
          ordering: '-created_at',
        });
        const results = (res.data && res.data.results) || res.results || res.data || [];
        this.admissionContacts = results.map(item => ({
          ...item,
          isSaving: false,
        }));
      } catch (e) {
        this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
      }
      this.isAdmissionContactsLoading = false;
    },
    addAdmissionContactRow() {
      this.admissionContacts.push({
        id: null,
        uni_offered_program: this.admissionContactsUniOfferedProgramId,
        type: null,
        type_other: '',
        name: '',
        title: '',
        email: '',
        phone: '',
        notes: '',
        isSaving: false,
      });
    },
    async saveAdmissionContact(idx) {
      const contact = this.admissionContacts[idx];
      if (!contact) return;
      const payload = {
        uni_offered_program: this.admissionContactsUniOfferedProgramId,
        type: contact.type,
        type_other: contact.type === 'Other' ? contact.type_other : '',
        name: contact.name,
        title: contact.title,
        email: contact.email,
        phone: contact.phone,
        notes: contact.notes,
      };
      this.$set(this.admissionContacts[idx], 'isSaving', true);
      try {
        if (contact.id) {
          await this.updateAdmissionContact({ id: contact.id, data: payload });
          this.makeToast({ variant: 'success', msg: 'Admission contact updated.' });
        } else {
          const resp = await this.createAdmissionContact(payload);
          const created = (resp.data && resp.data.data) || resp.data || {};
          this.$set(this.admissionContacts[idx], 'id', created.id || null);
          this.makeToast({ variant: 'success', msg: 'Admission contact added.' });
        }
      } catch (e) {
        this.makeToast({
          variant: 'danger',
          msg: get(e, 'response.data.message') || this.$t('generalMsgs.genErrorMsg'),
        });
      }
      this.$set(this.admissionContacts[idx], 'isSaving', false);
    },
    async removeAdmissionContact(idx) {
      const contact = this.admissionContacts[idx];
      if (!contact) return;
      if (!contact.id) {
        this.admissionContacts.splice(idx, 1);
        return;
      }
      try {
        await this.deleteAdmissionContact(contact.id);
        this.admissionContacts.splice(idx, 1);
        this.makeToast({ variant: 'success', msg: 'Admission contact removed.' });
      } catch (e) {
        this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
      }
    },
    addUniOfferedPg() {
      this.uni.uni_offered.push({
        program: '',
        enroll_link: '',
        description: '',
        how_to_title: '',
        how_to_link: '',
        how_to_desc: '',
      });
      this.syncChecklistProgramsFromUniversityPrograms();
    },
    removeUniOfferedPg(index) {
      this.uni.uni_offered.splice(index, 1);
      this.syncChecklistProgramsFromUniversityPrograms();
    },
    extractYoutubeID(url) {
      try {
        let r;
        const rx = constants.youtubeLinkRegex;
        r = url.match(rx);
        return r?.[1];
      } catch (e) {
        return '';
      }
    },
    isYoutubeVideoLinkValid(url) {
      return this.extractYoutubeID(url)?.length === 11;
    },
    async getScholarships() {
      this.areSchLoading = true;
      if (this.uni.offered_programs.length) {
        const scholarships = await this.getAllNewScholarships({ programs: this.uni.offered_programs });
        this.allScholarships = scholarships.data;

        if (this.uni.offered_programs.length < this.prevLength) {
          this.uni.scholarships = this.uni.scholarships.filter(scholarship => {
            return !!intersection(this.uni.offered_programs, scholarship.programs).length;
          });

          this.prevLength = this.uni.offered_programs.length;
        } else {
          this.prevLength = this.uni.offered_programs.length;
        }
      } else if (!this.uni.offered_programs.length) {
        this.allScholarships = [];
        this.uni.scholarships = [];
        this.prevLength = this.uni.offered_programs.length;
      }
      this.areSchLoading = false;
    },
    async fetchPrograms() {
      this.areProgramsLoading = true;

      const [allResponse, degreeResponse, certificateResponse, prepCourseResponse] = await Promise.all([
        this.getAllPrograms({ ordering: 'id' }),
        this.getAllPrograms({ ordering: 'id', prg_type__in: 'degree,advanced_certificate' }),
        this.getAllPrograms({ ordering: 'id', prg_type: 'certificate' }),
        this.getAllPrograms({ ordering: 'id', prg_type: 'prep_course' }),
      ]);
      this.allPrograms = allResponse.data;
      this.degreePrograms = degreeResponse.data;
      this.certificatePrograms = certificateResponse.data;
      this.prepCoursePrograms = prepCourseResponse.data;
      this.areProgramsLoading = false;
    },
    async onSubmit() {
      this.isLoading = true;

      try {
        const checklistGeneral = this.getChecklistGeneralPayload();
        const checklistPrograms = this.getChecklistProgramsPayload();

        const data = {
          name: this.uni.name,
          contact_email: this.uni.contact_email,
          phone: this.uni.phone,
          zipcode: this.uni.zipcode,
          address: this.uni.address,
          city: this.uni.city,
          state: this.uni.state,
          country: this.uni.country,
          campus_img_url: this.uni.logoUrl,
          small_logo_url: this.uni.small_logo_url,
          profile_bg_img_url: this.uni.profileBgImgUrl,
          primary_color: this.uni.primaryColor,
          secondary_color: this.uni.secondaryColor,
          dark_color: this.uni.darkColor,
          dark_color_rgb: hexRgb(this.uni.darkColor, { alpha: 0.5 }),
          bio: this.uni.bio,
          short_bio: this.uni.short_bio,
          offered_programs: this.uni.offered_programs,
          offering: (this.uni.offerings || []).map(offering => offering.value),
          other_offering: this.uni.otherOfferings,
          majors: this.uni.majors,
          scholarships: this.uni.scholarships.map(scholarship => scholarship.id),
          community_link: this.communityLink ? getClickableLink(this.communityLink) : '',
          uni_offered:
            this.uni.uni_offered.length > 1 ||
            (this.uni.uni_offered.length === 1 &&
              this.uni.uni_offered[0].program &&
              this.uni.uni_offered[0].enroll_link)
              ? this.uni.uni_offered
              : [],
          avg_prog_weeks: this.uni.avg_prog_weeks,
          tuition_to: this.uni.tuition_to,
          tuition_from: this.uni.tuition_from,
          pass_rate: this.uni.pass_rate,
          website: this.uni.website,
          institution_type: this.uni.institution_type,
          ...(checklistGeneral.length && { checklist_general: checklistGeneral }),
          ...(checklistPrograms.length && { checklist_programs: checklistPrograms }),
        };

        const uniLinks = data.uni_offered
        for(let link of uniLinks || []){
          if(get(link,'how_to_video') && !this.isYoutubeVideoLinkValid(link.how_to_video)){
            this.makeToast({ variant: 'danger', msg: 'Invalid Youtube video Link' });
            this.isLoading = false;
            return;
            
          }
        }

        if (this.$route.params.id) {
          await this.updateUni({
            id: this.$route.params.id,
            data,
          });
          this.makeToast({ variant: 'success', msg: 'University Updated!' });
        } else {
          await this.createUni({
            ...data,
          });
          this.makeToast({ variant: 'success', msg: 'University Added!' });
        }
       setTimeout(() => this.$router.push({ name: 'manage-unis' }), 250);
      } catch (err) {
        this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
      }

      this.isLoading = false;
    },

    async uploadImage(uploadType) {
      this.uploadCancelTokenSource[uploadType] = axios.CancelToken.source();
      this.isLogoUploading = uploadType === 'logo';
      this.isSmallLogoUploading = uploadType === 'smallLogo';
      this.isProfileBgImgUploading = uploadType === 'profileBgImage';
      try {
        const urlResp = await this.getUniImageUploadPresignedUrl({
          file_name: this.uploadFile[uploadType].name,
          content_type: this.uploadFile[uploadType].type,
        });
        await this.uploadToPresignedUrl({
          url: urlResp.upload_url,
          file: this.uploadFile[uploadType],
          config: {
            onUploadProgress: function(progressEvent) {
              this.uploadPercent[uploadType] = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            }.bind(this),
            cancelToken: this.uploadCancelTokenSource[uploadType].token,
          },
        });
        if (uploadType === 'logo') {
          this.uni.logoUrl = urlResp.upload_url.split('?')[0];
        } else if (uploadType === 'smallLogo') {
          this.uni.small_logo_url = urlResp.upload_url.split('?')[0];
        } else {
          this.uni.profileBgImgUrl = urlResp.upload_url.split('?')[0];
        }
      } catch (error) {
        this.uploadFile[uploadType] = null;
      }

      this.uploadCancelTokenSource[uploadType] = null;
      this.uploadPercent[uploadType] = 0;

      if (uploadType === 'logo') this.isLogoUploading = false;
      else if (uploadType === 'smallLogo') this.isSmallLogoUploading = false;
      else this.isProfileBgImgUploading = false;
    },
  },

  async mounted() {
    this.isLoading = true;
    // this.fetchScholarships();
    await this.fetchPrograms();
    this.checklistGeneral = this.getDefaultChecklistTemplate();
    this.syncChecklistProgramsFromUniversityPrograms();
    var usStates = new UsaStates();
    this.stateOptions = this.stateOptions.concat(
      map(usStates.states, state => ({
        value: state.name,
        text: state.name,
      })),
    );
    this.countryOptions = this.countryOptions.concat(
      map(countries, country => ({
        value: country.name,
        text: country.name,
      })),
    );

    const mappedOfferings = UNI_OFFERINGS.filter(offering => offering.options).map(offering => offering.options).flat();

    try {
      if (this.$route.params.id) {
        const resp = (await this.getUni(this.$route.params.id)).data;
        this.uni.name = resp.name;
        this.uni.contact_email = resp.contact_email;
        this.uni.phone = resp.phone;
        this.uni.zipcode = resp.zipcode;
        this.uni.address = resp.address;
        this.uni.city = resp.city;
        this.uni.state = resp.state;
        this.uni.country = resp.country;
        this.uni.logoUrl = resp.campus_img_url;
        this.uni.small_logo_url = resp.small_logo_url;
        this.uni.profileBgImgUrl = resp.profile_bg_img_url;
        this.uni.primaryColor = resp.primary_color;
        this.uni.secondaryColor = resp.secondary_color;
        this.uni.darkColor = resp.dark_color;
        this.uni.bio = resp.bio;
        this.uni.short_bio = resp.short_bio;
        this.uni.institution_type = resp.institution_type;
        this.uni.website = resp.website;
        this.uni.uni_offered = resp.uni_offered.length
          ? resp.uni_offered
          : [
              {
                program: '',
                enroll_link: '',
                description: '',
                how_to_title: '',
                how_to_link: '',
                how_to_desc: '',
              },
            ];
        this.uni.majors = resp.majors;
        this.uni.offered_programs = resp.offered_programs;
        this.uni.offerings = (resp.offering || []).map(offering => mappedOfferings.find(off => off.value === offering));
        this.uni.otherOfferings = resp.other_offering;
        this.uni.scholarships = resp.scholarships;
        this.communityLink = resp.community_link;
        this.uni.avg_prog_weeks = resp.avg_prog_weeks;
        this.uni.tuition_to = resp.tuition_to;
        this.uni.tuition_from = resp.tuition_from;
        this.uni.pass_rate = resp.pass_rate;
        this.checklistGeneral = this.mergeChecklistWithDefaults(resp.checklist_general || resp.general_checklist || []);
        this.checklistPrograms = (resp.checklist_programs || resp.program_checklists || []).map(group => ({
          program: group.program || group.program_id || null,
          // Mirror the general-scope load: merge each saved program checklist
          // with the current defaults so per-program shows every item too.
          checklist: this.mergeChecklistWithDefaults(group.checklist || []),
        }));
        this.syncChecklistProgramsFromUniversityPrograms();
        if (this.uni.offered_programs.length) {
          this.getScholarships();
        }
      }
    } catch (e) {
      this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
      this.$router.push({ name: 'manage-unis' });
    }

    this.isLoading = false;
  },
};
</script>
<style scoped>
.choose-options
 ::v-deep 
  .multiselect__tag {
    font-size: 0.8125rem !important;
    background-color: #f0f0f0 !important;
    color: black !important;
}
.multiselect .multiselect__tags .multiselect__tag {
  font-size: 0.8125rem !important;
  background-color: #f0f0f0 !important;
  color: black !important;
}
.multiselect__tags .multiselect__tag {
  font-size: 0.8125rem !important;
  background-color: #f0f0f0 !important;
  color: black !important;
}

.multiselect, .multiselect__input, .multiselect__single {
  font-size: 0.8125rem;
}

.checklist-config-card .checklist-scope-picker {
  min-width: 260px;
  max-width: 320px;
  flex: 1 1 260px;
}
.checklist-item-row {
  border: 1px solid #e3e7ed;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 12px;
  background: #fff;
  transition: border-color 0.15s ease, background 0.15s ease;
}
.checklist-item-row:hover {
  border-color: #c9d3e0;
}
.checklist-item-row--disabled {
  background: #f8f9fb;
  border-style: dashed;
}
.checklist-item-row--disabled .checklist-item-row__header .font-weight-600 {
  color: #8a93a2;
}
.checklist-item-row__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
  padding-bottom: 10px;
  border-bottom: 1px dashed #e3e7ed;
  flex-wrap: wrap;
  gap: 8px;
}
.checklist-item-row__icon {
  color: #2f6fed;
  font-size: 20px;
}
.checklist-item-row .form-label {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.02em;
  margin-bottom: 4px;
}
</style>
